'use client';

import React, { memo, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Card } from '@/shared/ui/shadcn/card';
import { AIGlowBorder, PulsingDots } from '@/shared/ui';
import { Heart, RotateCcw, ImageIcon, Loader2 } from 'lucide-react';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Progress } from '@/shared/ui/shadcn/progress';
import { cn } from '@/shared/lib/cn';
import type { HistoryItem } from '@/features/design/history';
import type { Concept } from '@/types/concept';
import type { ConceptCommon } from '@/types/concept-common';
import { RenderStatus } from '@/shared/constants/status';
import nextDynamic from 'next/dynamic';
import {
  useActiveJobForConcept,
  useActiveJobForConceptAngle,
} from '@/features/design/job-queue';
import { useToggleFavorite } from '@/features/design/history';
import { calculateTimeBasedProgress } from '@/shared/lib/render-status';

const ConceptActions = nextDynamic(
  () => import('./ConceptActions').then((m) => ({ default: m.ConceptActions })),
  { loading: () => <div className="w-8 h-8" /> }
);

interface ConceptCardProps {
  concept: HistoryItem | Concept | ConceptCommon;
  projectId?: string;
  projectName?: string;
  onDeleted?: () => void;
  onFavoriteToggle?: (conceptId: string, isFavorite: boolean) => void;
  hideActions?: boolean;
  /** Compact variant for carousel - no action bar, label below image */
  variant?: 'default' | 'compact';
  /**
   * Whether to render the label block (title + meta line) under the image.
   * The dashboard carousel passes `false` to show the image only and keep the
   * home screen visually clean — parity with Apple/Android dashboard cards.
   * The History grid keeps it `true`.
   */
  showLabel?: boolean;
}

export const ConceptCard = memo(function ConceptCard({
  concept,
  projectId: propProjectId,
  // projectName is still accepted by callers but no longer rendered —
  // the card subtitle now shows the concept reference, not the project name.
  projectName: _propProjectName,
  onFavoriteToggle,
  hideActions = false,
  variant = 'default',
  showLabel = true,
}: ConceptCardProps) {
  const t = useTranslations('render.queue');
  const tCard = useTranslations('conceptCard');
  const tConcept = useTranslations('concept');
  const router = useRouter();
  const conceptId = concept.id;
  const projectId = propProjectId || concept.relationships?.project?.data?.id;

  // Type-safe attribute extraction - works with all concept types
  interface ConceptAttributes {
    image_url?: string;
    main_angle_url?: string | null;
    main_render_url?: string | null;
    angle_url?: string; // Angle URL for failed render display (from history API)
    // Concept resource ships `renders_count` (plural); the history resource ships
    // `render_count` (singular). This card renders both — read both below.
    renders_count?: number;
    render_count?: number;
    has_multiplerenders?: boolean;
    is_favorite?: boolean;
    project_sequence_number?: number;
    name?: string;
    title?: string;
    image_type?: 'render' | 'angle';
    render_id?: string | null;
    is_upscaled?: boolean;
    activerender?: {
      id: string;
      status: string;
      started_at?: string;
      duration_predicted?: number;
      progress?: number;
    } | null;
    is_draft?: boolean;
  }

  const attrs = concept.attributes as ConceptAttributes & {
    is_main_angle?: boolean;
    mode?: 'explore' | 'refine';
  };

  // For history items `concept.id` is the render/album ID — read the navigation
  // IDs from the top-level fields populated by the history transform. For
  // regular Concept/ConceptCommon items `concept.id` IS the concept ID.
  // Mirrors Apple `HistoryItem.conceptId` / Android `HistoryItem.conceptId`.
  const isHistoryItem = concept.type === 'concept-history';
  const historyItem = isHistoryItem ? (concept as HistoryItem) : null;
  const navigationConceptId = historyItem ? historyItem.conceptId : conceptId;
  const imageUrl =
    attrs.image_url ?? attrs.main_render_url ?? attrs.main_angle_url ?? '';
  const renderCount = attrs.renders_count ?? attrs.render_count ?? 0;
  const isFavorite = attrs.is_favorite ?? false;
  const projectSequenceNumber = attrs.project_sequence_number ?? 0;
  const conceptName = attrs.name ?? attrs.title ?? '';
  const imageType =
    attrs.image_type ?? (renderCount === 0 ? 'angle' : 'render');
  const renderId = attrs.render_id ?? null;
  const isUpscaled = attrs.is_upscaled ?? false;
  const isMainAngle = attrs.is_main_angle ?? true;
  const conceptAngleId = historyItem ? historyItem.conceptAngleId : undefined;

  // Build concept URL with optional angleId for derivative angles
  const conceptUrl = (() => {
    const base = `/project/${projectId}/concept/${navigationConceptId}`;
    if (!isMainAngle && conceptAngleId) {
      return `${base}?angleId=${conceptAngleId}`;
    }
    return base;
  })();

  // Get active render from queue — per-angle when available, per-concept as fallback
  // Matches Android/Apple 3-tier lookup: angle-specific first, concept-level for main angles without angle ID
  const activeRenderByAngle = useActiveJobForConceptAngle(conceptAngleId);
  const activeRenderByConcept = useActiveJobForConcept(
    // Only use concept-level lookup when no conceptAngleId available (legacy concept history items)
    conceptAngleId ? undefined : navigationConceptId
  );
  const activeRenderFromQueue = activeRenderByAngle ?? activeRenderByConcept;

  // Fallback to concept attributes if not in queue
  const activeRenderFromAttrs = attrs.activerender ?? null;

  // Use queue render if available, otherwise fallback - transform to expected format
  const activeRender = activeRenderFromQueue
    ? {
        id: activeRenderFromQueue.id,
        status: activeRenderFromQueue.status,
        started_at: activeRenderFromQueue.startedAt,
        duration_predicted: activeRenderFromQueue.durationPredicted,
        progress: calculateTimeBasedProgress(
          activeRenderFromQueue.startedAt,
          activeRenderFromQueue.durationPredicted,
          activeRenderFromQueue.id
        ),
      }
    : activeRenderFromAttrs;

  if (!projectId || !navigationConceptId) {
    console.warn('ConceptCard: missing projectId or conceptId', {
      itemId: conceptId,
      projectId,
      navigationConceptId,
    });
    return null;
  }

  // Concept reference: "Concept #N. Name" if custom name, "Concept #N" otherwise.
  // Mirrors Apple HistoryItem.displayName (uses L10n.Concept.conceptNumber).
  const conceptRef = conceptName
    ? tConcept('conceptNumberWithTitle', {
        number: projectSequenceNumber,
        title: conceptName,
      })
    : tConcept('conceptNumber', { number: projectSequenceNumber });
  // Card title — type of view (mirrors Apple/Android history cards).
  const cardTitle = isMainAngle
    ? tCard('titleMainAngle')
    : tCard('titleDerivativeAngle');

  // Job status helpers (matching Android logic)
  const isJobActive =
    activeRender?.status === RenderStatus.PENDING ||
    activeRender?.status === RenderStatus.PROCESSING;
  const isJobFailed = activeRender?.status === RenderStatus.FAILED;
  const hideOverlayBadges = isJobActive || isJobFailed;

  // Concept-level card (no specific angle) vs history render card
  // Concept cards show spinner (multiple jobs may run simultaneously),
  // history cards show detailed percentage progress
  const isConceptCard = !conceptAngleId;

  // No-render state: concept has no completed render to display
  const hasNoRender = !attrs.main_render_url && !attrs.image_url;
  const isDraft = (attrs.is_draft ?? false) && !isJobActive && !isJobFailed;
  const isDesaturated = (isDraft || hasNoRender) && !isJobActive && !isJobFailed;

  // Progress overlay — concept cards show spinner/failed, history cards show detailed progress
  const progressOverlay = isConceptCard ? (
    <ConceptActivityOverlay isActive={isJobActive} isFailed={isJobFailed} t={t} />
  ) : (
    <RenderProgressOverlay
      activeRender={activeRender}
      angleUrl={attrs.main_angle_url || attrs.angle_url}
      t={t}
    />
  );

  // Autonomous favorite toggle (fallback when parent doesn't provide callback)
  const toggleFavoriteMutation = useToggleFavorite();
  const favoriteDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (favoriteDebounceRef.current) clearTimeout(favoriteDebounceRef.current);
    };
  }, []);

  const handleToggleFavorite = useCallback(() => {
    if (onFavoriteToggle) {
      onFavoriteToggle(navigationConceptId, isFavorite);
      return;
    }
    if (!projectId) return;

    // Debounce the API call — cancel any pending call first
    if (favoriteDebounceRef.current) clearTimeout(favoriteDebounceRef.current);
    const newValue = !isFavorite;
    favoriteDebounceRef.current = setTimeout(() => {
      toggleFavoriteMutation.mutate({
        projectId,
        conceptId: navigationConceptId,
        newIsFavorite: newValue,
      });
    }, 300);
  }, [onFavoriteToggle, navigationConceptId, isFavorite, projectId, toggleFavoriteMutation]);

  const handleViewDetails = () => {
    router.push(conceptUrl);
  };

  // Compact variant for carousel - unchanged from original
  if (variant === 'compact') {
    return (
      <Link
        href={conceptUrl}
        className="block group"
      >
        <AIGlowBorder isActive={activeRender?.status === RenderStatus.PROCESSING}>
          <Card className="overflow-hidden">
            <div className="aspect-[4/3] relative overflow-hidden bg-muted">
              {imageUrl ? (
                <>
                  <div className="absolute inset-0">
                    <Image
                      src={imageUrl}
                      alt={conceptRef}
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                      className={cn(
                        'object-cover group-hover:scale-105 transition-transform duration-300',
                        isDesaturated && 'saturate-[0.25] brightness-95 opacity-60'
                      )}
                    />
                  </div>

                  {/* Badges — hidden during active/failed jobs */}
                  {!hideOverlayBadges && (
                    <div className="absolute top-2 left-2 right-2 flex justify-between items-start z-20">
                      {/* Left badge: draft state only. Render-mode badge intentionally
                          omitted in the compact (recent designs) card to reduce overlay clutter. */}
                      {isDraft && (
                        <Badge className="text-chip bg-muted/90 text-muted-foreground hover:bg-muted/90">
                          {tCard('draft')}
                        </Badge>
                      )}

                      {/* Favorite badge — right */}
                      {isFavorite && (
                        <div className="ml-auto bg-white/90 rounded-full p-1">
                          <Heart className="h-3 w-3 fill-primary text-primary" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Progress overlays */}
                  {progressOverlay}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {!hideOverlayBadges && (
                    <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                      <ImageIcon className="h-8 w-8" />
                      <span className="text-chip">
                        {tCard('noAngle')}
                      </span>
                    </div>
                  )}
                  {progressOverlay}
                </div>
              )}
            </div>
          </Card>
        </AIGlowBorder>
        {/* Labels below image — omitted on the dashboard carousel (showLabel=false) */}
        {showLabel && (
          <div className="mt-2 px-1">
            <p className="text-list-item-value truncate">{cardTitle}</p>
            <p className="text-list-item-meta text-muted-foreground truncate">
              {conceptRef}
            </p>
          </div>
        )}
      </Link>
    );
  }

  // Default variant — matching Android HistoryItemCard structure.
  // No Card wrapper: the image rounds itself and the info section sits on the
  // page background — parity with AlbumCard/EstimateHistoryCard (and the other
  // platforms), which never paint a white footer under the label.
  return (
      <div className="group">
        {/* Image area */}
        <Link
          href={conceptUrl}
          className="block"
        >
          <AIGlowBorder isActive={activeRender?.status === RenderStatus.PROCESSING} className="aspect-[4/3] overflow-hidden rounded-lg shadow-sm ring-1 ring-border/50 bg-muted">
            {imageUrl ? (
              <>
                <div className="absolute inset-0">
                  <Image
                    src={imageUrl}
                    alt={conceptRef}
                    fill
                    sizes="(max-width: 640px) 50vw, (max-width: 768px) 33vw, 25vw"
                    className={cn(
                      'object-cover group-hover:scale-105 transition-transform duration-300',
                      isDesaturated && 'saturate-[0.25] brightness-95 opacity-60'
                    )}
                  />
                </div>

                {/* Badges — hidden during active/failed jobs.
                    Type-of-view info now lives in the card title (mirrors Apple history card),
                    so on the History grid only Draft + Favorite remain. */}
                {!hideOverlayBadges && (
                  <>
                    {isDraft && (
                      <Badge className="absolute top-2 left-2 z-20 text-chip bg-muted/90 text-muted-foreground hover:bg-muted/90">
                        {tCard('draft')}
                      </Badge>
                    )}

                    {/* Favorite badge — top-right */}
                    {isFavorite && (
                      <div className="absolute top-2 right-2 z-20 bg-white/80 rounded-full p-1">
                        <Heart className="h-3.5 w-3.5 fill-primary text-primary" />
                      </div>
                    )}
                  </>
                )}

                {/* Progress overlays */}
                {isConceptCard ? (
                  <ConceptActivityOverlay isActive={isJobActive} isFailed={isJobFailed} t={t} />
                ) : (
                  <RenderProgressOverlay
                    activeRender={activeRender}
                    angleUrl={attrs.main_angle_url || attrs.angle_url}
                    t={t}
                  />
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {!hideOverlayBadges && (
                  <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-chip">
                      {tCard('noAngle')}
                    </span>
                  </div>
                )}
                {isConceptCard ? (
                  <ConceptActivityOverlay isActive={isJobActive} isFailed={isJobFailed} t={t} />
                ) : (
                  <RenderProgressOverlay
                    activeRender={activeRender}
                    angleUrl={attrs.main_angle_url || attrs.angle_url}
                    t={t}
                  />
                )}
              </div>
            )}
          </AIGlowBorder>
        </Link>

        {/* Info section — matching Android HistoryItemCard layout */}
        <div className="flex items-center px-2 pt-2 pb-1 gap-1">
          <div className="flex-1 min-w-0">
            <p className="text-list-item-value truncate">{cardTitle}</p>
            <p className="text-list-item-meta text-muted-foreground truncate">
              {conceptRef}
            </p>
          </div>

          {/* 3-dot menu — only for renders, matching Android */}
          {!hideActions && (
            <ConceptActions
              projectId={projectId}
              conceptId={navigationConceptId}
              conceptName={conceptName}
              conceptSequenceNumber={projectSequenceNumber || undefined}
              renderFileUrl={imageType === 'render' ? imageUrl : null}
              renderId={renderId}
              isUpscaled={isUpscaled}
              isFavorite={isFavorite}
              isMainAngle={isMainAngle}
              onToggleFavorite={handleToggleFavorite}
              onViewDetails={handleViewDetails}
            />
          )}
        </div>
      </div>
  );
});

/**
 * Concept-level activity overlay — spinner for active, retry icon for failed.
 * Multiple jobs (renders + albums) may run simultaneously in a concept,
 * so we show a simple spinner instead of per-job progress.
 * Mirrors Apple ConceptCard.swift (indeterminate ProgressView) and
 * Android ConceptCardCompact ConceptGenerationOverlay.
 */
export function ConceptActivityOverlay({
  isActive,
  isFailed,
  t,
}: {
  isActive: boolean;
  isFailed: boolean;
  t: (key: string) => string;
}) {
  if (!isActive && !isFailed) return null;

  return (
    <div className="absolute inset-0 z-30">
      <div className="absolute inset-0 bg-black/30" />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
        {isActive ? (
          <Loader2 className="h-6 w-6 text-white animate-spin" />
        ) : (
          <>
            <RotateCcw className="w-10 h-10 text-white drop-shadow-lg" />
            <span className="text-white text-list-item-value">
              {t('incomplete')}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Render progress overlay — shared between default and compact variants.
 * Matches Android RenderProgressOverlay behavior.
 */
export function RenderProgressOverlay({
  activeRender,
  angleUrl,
  t,
}: {
  activeRender: {
    status: string;
    progress?: number;
  } | null;
  angleUrl?: string | null;
  t: (key: string) => string;
}) {
  if (!activeRender) return null;

  if (
    activeRender.status === RenderStatus.PENDING ||
    activeRender.status === RenderStatus.PROCESSING
  ) {
    const isProcessing = activeRender.status === RenderStatus.PROCESSING;
    const progress = activeRender.progress ?? 0;
    const isStalling = progress >= 95;
    return (
      <div className="absolute inset-0 z-30">
        {/* Desaturated angle image background */}
        {angleUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${angleUrl})`,
              filter: 'brightness(0.85) saturate(0)',
            }}
          />
        )}
        {/* Dark overlay matching Apple's Color.black.opacity(0.15) */}
        {angleUrl && <div className="absolute inset-0 bg-black/15" />}
        {!angleUrl && <div className="absolute inset-0 bg-black/50" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          {isProcessing ? (
            <div className="text-center">
              <div className="text-white text-token-balance mb-2">
                {Math.round(progress)}%
              </div>
              <Progress
                value={progress}
                className={cn('w-16 h-1', isStalling && 'animate-pulse')}
              />
            </div>
          ) : (
            <>
              <PulsingDots size="lg" className="text-white" />
              <span className="text-white text-list-item-value">
                {t('queued')}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  if (activeRender.status === RenderStatus.FAILED) {
    return (
      <div className="absolute inset-0 z-30">
        {/* Blurred angle image background */}
        {angleUrl && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(${angleUrl})`,
              filter: 'blur(8px) brightness(0.85) saturate(0)',
            }}
          />
        )}
        {angleUrl && <div className="absolute inset-0 bg-black/15" />}
        {!angleUrl && <div className="absolute inset-0 bg-muted" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
          <RotateCcw className="w-10 h-10 text-white drop-shadow-lg" />
          <span className="text-white text-list-item-value">
            {t('incomplete')}
          </span>
        </div>
      </div>
    );
  }

  return null;
}
